import React  from 'react';
import {connect} from "react-redux";
import XLSX from 'xlsx';
import {Alert, Button} from "react-bootstrap";
import Modal from "react-bootstrap/es/Modal";
import {Well, ControlLabel, Checkbox, FormGroup, FormControl, HelpBlock} from "react-bootstrap";
import PropTypes from "prop-types";
import {API_CALL_FAILED} from "../../redux/action-types";

/**
 * A custom excel importer which is dynamic in its columns.
 */
class DynamicExcelTools extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            data: [],
            confirmationRequired: false,
            showModal: false,
            errors: [],
            done: false,
            current: null,
            overrideSupplier: '',
            createObject: true,
            enrichData: true
        };
        this.handleFile = this.handleFile.bind(this);
    };

    componentDidUpdate(prevProps, prevState, prevContext) {
        if(this.state.importing && this.props.lastAction && this.props.lastAction.type === API_CALL_FAILED) {
            this.setState({
                importing: false,
                failedAt: this.state.current,
                errors: [
                    "Failed to import "+this.props.subject+" row "+(this.state.current+2)+".",  // Plus 1 for the header + 1 for human numbers = plus 2
                    this.props.lastAction.payload.message ]
            })
        }
    }

    handleFile(file) {
        const reader = new FileReader();
        const rABS = !!reader.readAsBinaryString;
        reader.onload = (e) => {
            const bstr = e.target.result;
            const wb = XLSX.read(bstr, {type:rABS ? 'binary' : 'array'});
            const ws = wb.Sheets[wb.SheetNames[0]];
            const data = XLSX.utils.sheet_to_json(ws, {header:1}).filter(d => d.length > 0);    // Filter out empty rows
            this.convertDataForImport(data);
        };
        if(rABS) reader.readAsBinaryString(file); else reader.readAsArrayBuffer(file);
    };

    convertDataForImport(data) {
        // check that we have the proper column keys in our excel data
        let suppliedColumns = data.shift();
        let errors = [];
        let missingColumnError = column => "Missing the required column '"+column+"'.";

        function mustBePresent(columnName) {
            if (suppliedColumns.indexOf(columnName) === -1) {
                errors.push(missingColumnError(columnName))
            }
        }

        this.props.requiredColumns.forEach(col => mustBePresent(col));

        let rows = [];

        if(errors.length === 0) {
            for(let d = 0; d < data.length; d++) {
                let row = {};
                for(let i in suppliedColumns) {
                    if(suppliedColumns.hasOwnProperty(i)) {
                        row[suppliedColumns[i]] = data[d][i];
                    }
                }
                rows.push(row);
            }
        }

        this.setState({
            errors: errors,
            confirmationRequired: errors.length === 0,
            data: rows,
            done: false
        });
    }

    serializeParams = paramsObj => Object.keys(paramsObj).map(key => `${key}=${encodeURIComponent(paramsObj[key])}`).join('&')
    
    importFile(e, startAt) {
        e.preventDefault();
        this.setState({
            importing: true,
            current: startAt,
            confirmationRequired: false
        });

        this.props.dispatch(this.props.startImportAction);
        this.postSingle(startAt);    // Post the data, starting with 0.
    }

    postSingle(current) {
        if(this.state.importing === true || current === 0  || current === this.state.failedAt) {   // make it possible to stop posting when the modal is closed.
            const singleEntry = this.possiblyClean(this.state.data[current])
            const paramsObj = {
                enrichData: this.state.enrichData
            }

            if (this.state.overrideSupplier) {
                paramsObj['overrideSupplier'] = this.state.overrideSupplier
            }

            if (this.props.subject === 'valuation') {
                paramsObj['createObject'] = this.state.createObject
            }

            const query = `?${this.serializeParams(paramsObj)}`

            console.log(query)

            this.props.postSingleEntry(singleEntry, query, function (d) {
                if ((current + 1) < this.state.data.length) {
                    this.setState({current: current + 1});
                    this.postSingle(current + 1)
                }
                else {
                    this.setState({
                        importing: false,
                        done: true
                    });
                }
            }.bind(this))
        }
        else {
            console.warn("Stop posting...");
        }
    }

    possiblyClean(row) {
        if(this.props.purgeEmptyAttributes) {
            Object.keys(row).forEach((key) => (row[key] == null || (row[key].trim && row[key].trim() === "")) && delete row[key]);
        }
        return row;
    }

    closeModal(e) {
        if(e && e.preventDefault) e.preventDefault();
        this.setState({
            showModal: false,
            confirmationRequired: false,
            importing: false,
            done: false
        });
    }

    render() {
        return (
            <div>
                <div onClick={e => this.setState({ showModal: true })}>
                    <i className="fas fa-file-excel"/>
                </div>
                <Modal show={this.state.showModal} onHide={e => this.closeModal(e)}>
                    <Modal.Header closeButton>
                        <Modal.Title>Import {this.props.subject}s</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <DragDropFile handleFile={this.handleFile}>
                            {this.state.errors && this.state.errors.length > 0 &&
                                <Alert className="pre-line mt-0" bsStyle="danger">
                                    {this.state.errors.map(e => <p key={e}>{e}</p>)}
                                </Alert>}

                            {this.state.done && <Alert className="pre-line mt-0" bsStyle="success">
                                <div>Completed import of {this.state.data.length} {this.props.subject}.</div>
                                <div>You may close this window.</div>
                            </Alert>}

                            {this.state.importing && <Alert className="pre-line mt-0" bsStyle="info">
                                <div>Importing {this.props.subject} {this.state.current + 1} / {this.state.data.length} ... </div>
                                <div>Close this window to cancel the import.</div>
                            </Alert>}

                            {this.state.confirmationRequired &&
                                <Alert className="pre-line mt-0" bsStyle="warning">
                                    Import {this.state.data.length} {this.props.subject}s?
                                </Alert>}

                            <span>
                                <p>Upload a file to import new data.</p>
                                {!this.state.importing &&
                                    <div><DataInput handleFile={this.handleFile}/></div>
                                }
                            </span>
                        </DragDropFile>
                        <br/>
                        <Well>
                            <FormGroup>
                                <h4>Options</h4>
                            </FormGroup>
                            {this.state.enrichData === true && <FormGroup>
                                <ControlLabel>Override Supplier</ControlLabel>
                                <FormControl
                                    componentClass="select"
                                    placeholder="select supplier..."
                                    value={this.state.overrideSupplier}
                                    onChange={e => this.setState({overrideSupplier: e.target.value})}>
                                    <option key='' value=''></option>
                                    <option key='google' value='google'>Google</option>
                                    <option key='bag' value='bag'>BAG</option>
                                </FormControl>
                                <HelpBlock>Optional: override the automatically determined address supplier.</HelpBlock>
                            </FormGroup>}

                            {this.props.subject === 'valuation' && <FormGroup>
                                <Checkbox
                                    checked={this.state.createObject}
                                    onChange={() => this.setState({createObject: !this.state.createObject})}>
                                    createObject
                                </Checkbox>
                                <HelpBlock>Should the first object in the valuation be created from the supplied data?</HelpBlock>
                            </FormGroup>}

                            <FormGroup>
                                <Checkbox
                                    checked={this.state.enrichData}
                                    onChange={() => this.setState({enrichData: !this.state.enrichData})}>
                                    enrichData
                                </Checkbox>
                                <HelpBlock>Should the data be enriched with data from address suppliers?</HelpBlock>
                            </FormGroup>
                        </Well>
                    </Modal.Body>
                    <Modal.Footer>
                        {this.state.confirmationRequired ?
                            <Button bsStyle="warning" onClick={e => this.importFile(e, 0)}>Import</Button>
                        : null }

                        {this.state.failedAt && this.state.confirmationRequired ?
                            <Button bsStyle="info" onClick={e => this.importFile(e, this.state.failedAt)}>Continue import</Button>
                            : null }
                        <Button onClick={e => this.closeModal(e)}>Close</Button>
                    </Modal.Footer>
                </Modal>
            </div>
        );
    };
};

DynamicExcelTools.propTypes = {
    subject: PropTypes.string.isRequired,
    fileName: PropTypes.string.isRequired,
    purgeEmptyAttributes: PropTypes.bool.isRequired,
    requiredColumns: PropTypes.array.isRequired,
    startImportAction: PropTypes.object.isRequired,
    postSingleEntry: PropTypes.func.isRequired
};


/* -------------------------------------------------------------------------- */

/*
  Simple HTML5 file drag-and-drop wrapper
  usage: <DragDropFile handleFile={handleFile}>...</DragDropFile>
    handleFile(file:File):void;
*/
class DragDropFile extends React.Component {
    constructor(props) {
        super(props);
        this.onDrop = this.onDrop.bind(this);
    };

    suppress(evt) {
        evt.stopPropagation();
        evt.preventDefault();
    };

    onDrop(evt) { evt.stopPropagation(); evt.preventDefault();
        const files = evt.dataTransfer.files;
        if(files && files[0]) this.props.handleFile(files[0]);
    };

    render() { return (
        <div onDrop={this.onDrop} onDragEnter={this.suppress} onDragOver={this.suppress}>
            {this.props.children}
        </div>
    ); };
};

/*
  Simple HTML5 file input wrapper
  usage: <DataInput handleFile={callback} />
    handleFile(file:File):void;
*/
class DataInput extends React.Component {
    constructor(props) {
        super(props);
        this.handleChange = this.handleChange.bind(this);
    };
    handleChange(e) {
        const files = e.target.files;
        if(files && files[0]) this.props.handleFile(files[0]);
    };
    render() { return (
        <form className="form-inline">
            <div className="form-group">
                <input type="file" className="form-control" id="file" accept={SheetJSFT} onChange={this.handleChange} />
            </div>
        </form>
    ); };
}

/* list of supported file types */
const SheetJSFT = [
    "xlsx", "xlsb", "xlsm", "xls", "xml", "csv", "txt", "ods", "fods", "uos", "sylk", "dif", "dbf", "prn", "qpw", "123", "wb*", "wq*", "html", "htm"
].map(function(x) { return "." + x; }).join(",");

const mapStateToProps = state => {
    return { lastAction: state.lastAction};
};

const mapDispatchToProps = (dispatch) => ({dispatch});

export default connect(mapStateToProps, mapDispatchToProps)(DynamicExcelTools);



// WEBPACK FOOTER //
// ./src/components/reusables/DynamicExcelTools.js